import { useState } from "react";
import { cn } from "@/lib/utils";
import { spacePreviewGradient } from "@/lib/spacePreview";
import { isPrivateVisibility, type SpaceSummary } from "@mind-sands/shared";

export interface SpaceCardProps {
  space: SpaceSummary;
  onEnter: (slug: string) => void;
  /** Lone card in a category — taller cover so full-width does not look like a missing pair. */
  featured?: boolean;
}

/**
 * Space gallery card — visual preview + name + description.
 * Shows the space thumbnail when available, otherwise a palette
 * gradient derived from the space's sky and time of day.
 */
export function SpaceCard({ space, onEnter, featured = false }: SpaceCardProps) {
  const [thumbFailed, setThumbFailed] = useState(false);
  const showThumb = Boolean(space.thumbnailUrl) && !thumbFailed;

  return (
    <button
      onClick={() => onEnter(space.slug)}
      disabled={!space.slug}
      className={cn(
        "group w-full text-left rounded-2xl overflow-hidden border border-sands-200 bg-sands-100",
        "transition-all duration-300 ease-out",
        "hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]",
        "motion-reduce:hover:scale-100 motion-reduce:active:scale-100",
        "focus-visible:ring-2 focus-visible:ring-sands-300 focus-visible:outline-none",
        "disabled:opacity-50 disabled:pointer-events-none",
      )}
    >
      <div
        className={cn(
          "relative w-full overflow-hidden",
          featured ? "h-48 sm:h-56" : "h-36",
        )}
        style={{ background: spacePreviewGradient(space) }}
      >
        {showThumb && (
          <img
            src={space.thumbnailUrl}
            alt={space.name}
            loading="lazy"
            onError={() => setThumbFailed(true)}
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-300 group-hover:opacity-95"
          />
        )}
        {isPrivateVisibility(space.visibility) && (
          <span className="absolute top-2 left-2 rounded-full bg-sands-800/80 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white">
            Hidden
          </span>
        )}
      </div>
      <div className="p-4">
        <p className="text-base font-medium text-sands-800 truncate">
          {space.name}
        </p>
        <p className="text-sm text-sands-500 mt-1 line-clamp-2 leading-relaxed">
          {space.description}
        </p>
      </div>
    </button>
  );
}
